import { Injectable } from '@angular/core';

// IP-to-country lookup table (src/assets/geoip) is DB-IP's "IP to Country Lite" dataset,
// licensed under CC BY 4.0 - https://db-ip.com/db/lite.php. Attribution required on redistribution.
@Injectable({
  providedIn: 'root'
})

export class GeoCountryService {

  private ends: Uint32Array | null = null;
  private codes: string = '';
  private loadPromise: Promise<void> | null = null;

  private async ensureLoaded(): Promise<void> {
    if (this.ends) {
      return;
    }
    if (!this.loadPromise) {
      // Retry on the next call if this attempt fails (e.g. asset not yet deployed).
      this.loadPromise = this.loadRanges().catch(error => {
        this.loadPromise = null;
        throw error;
      });
    }
    return this.loadPromise;
  }

  private async loadRanges(): Promise<void> {
    const response = await fetch('assets/geoip/ip-country-ranges.txt');
    if (!response.ok) {
      throw new Error(`geoip range table request failed with status ${response.status}`);
    }

    const text = await response.text();
    const lines = text.split(/\r?\n/).filter(line => line.length > 0);

    // Guard against an SPA/404 fallback handing us index.html with a 200 status:
    // every real line is "<base36-range-end> <ISO-3166 alpha-2 code>".
    if (lines.length === 0 || !/^[0-9a-z]+ [A-Z]{2}$/.test(lines[0])) {
      throw new Error('geoip range table response was not the expected data file');
    }

    const ends = new Uint32Array(lines.length);
    const codes = new Array<string>(lines.length);

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const spaceIndex = line.indexOf(' ');
      ends[i] = parseInt(line.substring(0, spaceIndex), 36);
      codes[i] = line.substring(spaceIndex + 1);
    }

    this.ends = ends;
    this.codes = codes.join('');
  }

  private ipToUint32(ip: string): number | null {
    const parts = ip.split('.');
    if (parts.length !== 4) {
      return null; // IPv6 or malformed input is not covered by this table
    }
    let value = 0;
    for (const part of parts) {
      const octet = Number(part);
      if (!Number.isInteger(octet) || octet < 0 || octet > 255) {
        return null;
      }
      value = (value * 256) + octet;
    }
    return value >>> 0;
  }

  // Ranges are stored as cumulative "end" boundaries covering the full IPv4 space,
  // so the country for `target` is whichever range's end is the first one >= target.
  private findRangeIndex(ends: Uint32Array, target: number): number {
    let low = 0;
    let high = ends.length - 1;
    while (low < high) {
      const mid = (low + high) >>> 1;
      if (ends[mid] < target) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    return low;
  }

  public async resolveCountry(ipAddress: string): Promise<string> {
    const target = this.ipToUint32(ipAddress);
    if (target === null) {
      return 'Unknown';
    }

    try {
      await this.ensureLoaded();
    } catch (error) {
      console.error('Unable to load geoip range table', error);
      return 'Unknown';
    }
    if (!this.ends) {
      return 'Unknown';
    }

    const index = this.findRangeIndex(this.ends, target);
    const countryCode = this.codes.substring(index * 2, index * 2 + 2);

    if (countryCode.length !== 2 || countryCode === 'ZZ') {
      return 'Unknown';
    }

    try {
      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
      return regionNames.of(countryCode) ?? countryCode;
    } catch {
      return countryCode;
    }
  }
}
